import React from "react";
import { Box, Button, Card, Chip, Grid, LinearProgress, Stack, Typography } from "@mui/joy";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import VolunteerActivismRoundedIcon from "@mui/icons-material/VolunteerActivismRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useTheme } from "@mui/joy/styles";

interface DashboardHeroProps {
  monthlyProgress?: number;
  sevaCount?: number;
}

export default function DashboardHero({ monthlyProgress = 72, sevaCount = 72 }: DashboardHeroProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const heroBackground = isDark
    ? "linear-gradient(180deg, rgba(15, 23, 42, 0.96) 0%, rgba(10, 15, 26, 0.98) 100%)"
    : "radial-gradient(120% 140% at 8% 0%, rgba(255, 218, 170, 0.2) 0%, rgba(255, 218, 170, 0) 55%), radial-gradient(120% 140% at 95% 5%, rgba(165, 197, 255, 0.2) 0%, rgba(165, 197, 255, 0) 60%), linear-gradient(180deg, rgba(255, 252, 245, 1) 0%, rgba(248, 250, 255, 1) 45%, rgba(244, 246, 252, 1) 100%)";
  const heroOverlay = isDark
    ? "radial-gradient(80% 100% at 110% 10%, rgba(56, 189, 248, 0.16) 0%, transparent 70%), radial-gradient(60% 60% at -10% 110%, rgba(251, 191, 36, 0.14) 0%, transparent 75%)"
    : "radial-gradient(80% 100% at 110% 10%, rgba(255,214,153,0.2) 0%, transparent 70%), radial-gradient(60% 60% at -10% 110%, rgba(175,205,255,0.2) 0%, transparent 75%)";
  const titleColor = isDark ? "rgba(248,250,252,0.92)" : "text.primary";
  const bodyColor = isDark ? "rgba(226,232,240,0.78)" : "text.secondary";
  const panelBg = isDark ? "rgba(15, 23, 42, 0.88)" : "background.surface";
  const panelBorder = isDark ? "rgba(148, 163, 184, 0.2)" : "divider";
  const panelTitle = isDark ? "rgba(248,250,252,0.9)" : "text.primary";
  const panelBody = isDark ? "rgba(226,232,240,0.72)" : "text.secondary";

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: "xl",
        p: { xs: 3, md: 4 },
        position: "relative",
        overflow: "hidden",
        minHeight: { md: 260 },
        borderColor: "divider",
        boxShadow: "lg",
        background: heroBackground,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: heroOverlay,
          pointerEvents: "none",
        }}
      />
      <Grid container spacing={3} alignItems="center">
        <Grid xs={12} md={7}>
          <Stack spacing={1.4} sx={{ position: "relative", zIndex: 1 }}>
            <Chip size="sm" variant="soft" color="neutral" sx={{ alignSelf: "flex-start", fontWeight: 600 }}>
              Together in seva & devotion
            </Chip>
            <Typography level="h1" sx={{ fontWeight: 800, letterSpacing: -0.5, color: titleColor }}>
              Hare Krishna, dear devotee!
            </Typography>
            <Typography level="body-lg" sx={{ maxWidth: 480, color: bodyColor }}>
              Your daily offerings are nurturing countless hearts. Continue your journey of seva with today’s opportunities.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Button
                size="md"
                variant="soft"
                color="primary"
                startDecorator={<VolunteerActivismRoundedIcon />}
              >
                Make an offering
              </Button>
              <Button
                size="md"
                variant="plain"
                color="neutral"
                endDecorator={<ArrowForwardRoundedIcon />}
              >
                View seva calendar
              </Button>
            </Stack>
          </Stack>
        </Grid>
        <Grid xs={12} md={5}>
          <Stack
            spacing={2}
            sx={{
              borderRadius: "xl",
              p: 3,
              bgcolor: panelBg,
              border: "1px solid",
              borderColor: panelBorder,
              position: "relative",
              zIndex: 1,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <CalendarTodayRoundedIcon fontSize="small" />
              <Typography level="title-sm" sx={{ fontWeight: 600, color: panelTitle }}>
                Today: Ekadashi seva focus
              </Typography>
            </Stack>
            <Typography level="body-sm" sx={{ color: panelBody }}>
              {sevaCount} devotees joined you in offering heartfelt prayers today.
            </Typography>
            <Stack spacing={0.75}>
              <Typography level="body-xs" sx={{ textTransform: "uppercase", letterSpacing: 0.8, color: panelBody }}>
                Monthly seva progress
              </Typography>
              <LinearProgress determinate value={monthlyProgress} sx={{ height: 8, borderRadius: 999 }} />
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </Card>
  );
}
